import { useState, useMemo } from 'react';
import {
  TextField,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  InputAdornment,
  CircularProgress,
  Autocomplete,
  Avatar,
  Alert,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-toastify';
import auth from '@/services/AuthService';
import tenant from '@/services/TenantService';
import ToastMessage from '@/components/toasts/ToastMessage';
import RegisterPaymentForm from '@/components/forms/register/RegisterPaymentForm';
import countries from 'world-countries';
import i18n from 'i18n-iso-countries';
import deLocale from 'i18n-iso-countries/langs/de.json';
import { Grid } from '@mui/system';
i18n.registerLocale(deLocale);
import logo from '../../../assets/logo.svg';
import background from '../../../assets/background.webp';
import { useNavigate } from 'react-router-dom';

export default function RegisterPage() {
  const [step, setStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    street: '',
    city: '',
    countryCode: '',
    countryName: '',
    zipcode: '',
  });

  const navigate = useNavigate();

  const countryList = countries.map((country) => ({
    code: country.cca2,
    name: i18n.getName(country.cca2, 'de') || country.name.common,
    flag: `https://flagcdn.com/w40/${country.cca2.toLowerCase()}.png`,
  }));

  const [tenantId, setTenantId] = useState('');
  const [userId, setUserId] = useState('');

  const steps = [
    'Firma & Adresse  ',
    'Persönliche Daten',
    'Zahlungs- informationen',
  ];

  const isStep0Valid = useMemo(() => {
    const { street, zipcode, company, countryCode, countryName, city } =
      formData;
    return (
      company.trim() !== '' &&
      street.trim() !== '' &&
      city.trim() !== '' &&
      zipcode.trim() !== '' &&
      countryCode.trim() !== '' &&
      countryName.trim() !== ''
    );
  }, [formData]);

  const isStep1Valid = useMemo(() => {
    const { firstName, lastName, email, password, confirmPassword } = formData;
    return (
      firstName.trim() !== '' &&
      lastName.trim() !== '' &&
      email.trim() !== '' &&
      password.trim() !== '' &&
      confirmPassword.trim() !== ''
    );
  }, [formData]);

  const toggleShowPassword = () => setShowPassword(!showPassword);

  const toggleShowConfirmPassword = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevState) => {
      const updatedFormData = { ...prevState, [name]: value };

      setEmailError(
        updatedFormData.email === '' || validateEmail(updatedFormData.email)
          ? ''
          : 'Bitte eine gültige E-Mail-Adresse eingeben.',
      );

      setPasswordError(
        updatedFormData.password === '' ||
          validatePassword(updatedFormData.password)
          ? ''
          : 'Mindestens 8 Zeichen',
      );

      setConfirmPasswordError(
        updatedFormData.confirmPassword !== updatedFormData.password
          ? 'Passwörter stimmen nicht überein.'
          : '',
      );

      return updatedFormData;
    });
  };

  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => prev - 1);

  const handleSubmit = async () => {
    setLoading(true);

    if (!formData.email || !validateEmail(formData.email)) {
      setEmailError('Bitte eine gültige E-Mail-Adresse eingeben.');
      return;
    }

    if (!formData.password || !validatePassword(formData.password)) {
      setPasswordError('Mindestens 8 Zeichen');
      setLoading(false);
      return;
    }

    if (formData.confirmPassword !== formData.password) {
      setConfirmPasswordError('Passwörter stimmen nicht überein.');
      setLoading(false);
      return;
    }

    const userData = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
    };

    const tenantData = {
      company: formData.company,
      street: formData.street,
      city: formData.city,
      zipcode: formData.zipcode,
      countryCode: formData.countryCode,
      countryName: formData.countryName,
    };
    let createdUserId;
    try {
      // Clear local storage to prevent token issues before registering
      localStorage.clear();
      const userResponse = await auth.post('/register', userData);
      createdUserId = userResponse.data.userId;
      const registrationToken = userResponse.data.registrationToken;
      localStorage.setItem('registrationToken', registrationToken);
      setUserId(createdUserId);

      const tenantResponse = await tenant.post('/tenant', {
        ...tenantData,
        userId: createdUserId,
      }, {
        headers: {
          Authorization: `Bearer ${registrationToken}`,
        },
      });
      const data = tenantResponse.data;
      setTenantId(data._id);
      setStep(2);
    } catch (error) {
      toast.error(
        <ToastMessage
          title="Fehler"
          subtitle={error?.response?.data?.message}
        />,
      );
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (password) => {
    return password.length >= 8;
  };

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };
  return (
    <Grid
      container
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
      }}
    >
      <Grid
        size={{ xs: false, sm: 4, md: 7 }}
        sx={{
          backgroundImage: `url(${background})`,
          backgroundRepeat: 'no-repeat',
          backgroundColor: (t) =>
            t.palette.mode === 'light'
              ? t.palette.grey[50]
              : t.palette.grey[900],
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <Grid
        size={{ xs: 12, sm: 8, md: 5 }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            py: 8,
            px: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            maxWidth: '480px',
            width: '100%',
          }}
        >
          <img
            style={{
              paddingBottom: '5px',
              height: '70px',
              userDrag: 'none',
              userSelect: 'none',
              pointerEvents: 'none',
            }}
            src={logo}
            alt="Codemeta Logo"
            id="logo"
          />

          <Typography variant="h4" align="center" gutterBottom sx={{ mt: 2 }}>
            Registrierung
          </Typography>
          <Stepper
            activeStep={step}
            alternativeLabel
            sx={{
              mt: 1,
              mb: 1,
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {step === 0 && (
            <Box
              display="flex"
              flexDirection="column"
              sx={{ width: '100%', gap: { xs: 2, sm: 2, md: 2, lg: 2 } }}
            >
              <TextField
                label="Firma"
                name="company"
                onChange={handleChange}
                value={formData.company}
                fullWidth
                required
              />
              <TextField
                label="Straße"
                name="street"
                onChange={handleChange}
                value={formData.street}
                fullWidth
                required
              />
              <TextField
                label="Stadt"
                name="city"
                onChange={handleChange}
                value={formData.city}
                fullWidth
                required
              />
              <TextField
                label="PLZ"
                name="zipcode"
                onChange={handleChange}
                value={formData.zipcode}
                fullWidth
                required
              />
              <Autocomplete
                fullWidth
                options={countryList}
                value={
                  formData.countryName
                    ? {
                        code: formData.countryCode,
                        name: formData.countryName,
                      }
                    : null
                }
                getOptionLabel={(option) => option.name}
                onChange={(event, newValue) =>
                  setFormData({
                    ...formData,
                    countryCode: newValue ? newValue.code : '',
                    countryName: newValue ? newValue.name : '',
                  })
                }
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Avatar
                      src={option.flag}
                      alt={option.name}
                      sx={{ width: 24, height: 16, borderRadius: 0 }}
                    />
                    {option.name}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Land" required />
                )}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={nextStep}
                fullWidth
                disabled={!isStep0Valid}
              >
                Weiter
              </Button>
            </Box>
          )}

          {step === 1 && (
            <Box
              display="flex"
              flexDirection="column"
              sx={{ width: '100%', gap: { xs: 2, sm: 2, md: 2, lg: 2 } }}
            >
              <TextField
                label="Vorname"
                name="firstName"
                onChange={handleChange}
                value={formData.firstName}
                fullWidth
                required
              />
              <TextField
                label="Nachname"
                name="lastName"
                onChange={handleChange}
                value={formData.lastName}
                fullWidth
                required
              />
              <TextField
                label="E-Mail"
                name="email"
                type="email"
                onChange={handleChange}
                value={formData.email}
                fullWidth
                required
                error={!!emailError}
                helperText={emailError}
              />
              <TextField
                label="Passwort"
                name="password"
                type={showPassword ? 'text' : 'password'}
                onChange={handleChange}
                value={formData.password}
                fullWidth
                required
                error={!!passwordError}
                helperText={passwordError}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={toggleShowPassword}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <TextField
                label="Passwort bestätigen"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                onChange={handleChange}
                value={formData.confirmPassword}
                fullWidth
                required
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={toggleShowConfirmPassword}
                          edge="end"
                          tabIndex={-1}
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Box display="flex" justifyContent="space-between">
                <Button variant="outlined" onClick={prevStep}>
                  Zurück
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmit}
                  disabled={!isStep1Valid || loading}
                >
                  {!isStep1Valid && loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Weiter zur Zahlung'
                  )}
                </Button>
              </Box>
            </Box>
          )}

          {step === 2 && (
            <Box sx={{ height: '540px', overflowY: 'auto' }}>
              <Box
                display="flex"
                flexDirection="column"
                sx={{
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <RegisterPaymentForm
                  tenantId={tenantId}
                  userId={userId}
                  sx={{ maxHeight: '60vh' }}
                  onSuccess={() => setStep(3)}
                />
              </Box>
            </Box>
          )}

          {step === 3 && (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              sx={{ width: '100%', gap: { xs: 2, sm: 2, md: 2, lg: 2 } }}
            >
              <Alert severity="info" sx={{ width: '100%' }}>
                <Typography variant="body1">
                  Ihr Konto wurde erfolgreich erstellt!
                </Typography>
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Bitte bestätigen Sie Ihre E-Mail-Adresse, indem Sie auf den Link
                in der Bestätigungs-E-Mail klicken, die wir Ihnen gerade
                gesendet haben. Im Anschluss können Sie sich anmelden und
                weitere Apps hinzufügen.
              </Typography>
              <Button fullWidth variant="contained" onClick={() => navigate('/organizer')}>
                Zur Anmeldung
              </Button>
            </Box>
          )}
        </Box>
      </Grid>
    </Grid>
  );
}
